import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Calculator, Leaf, Coffee, Wheat, RotateCcw } from 'lucide-react';

// Recettes de reference (base 100%)
const RECIPES = {
  jasmin: {
    nom: 'Thé Jasmin',
    icon: Leaf,
    color: 'text-green-600',
    bgColor: 'bg-green-50/50 border-green-200',
    pivotLabel: 'Thé Jasmin',
    pivotUnit: 'g',
    pivotBase: 50,
    ingredients: [
      { label: 'Eau', base: 5000, unit: 'mL' },
    ],
    notes: 'Faire bouillir l\'eau, arrêter le feu, mettre les sachets, infuser 15 min. DLC 2 jours.',
  },
  oolong: {
    nom: 'Thé Oolong',
    icon: Coffee,
    color: 'text-amber-700',
    bgColor: 'bg-amber-50/50 border-amber-200',
    pivotLabel: 'Thé Oolong',
    pivotUnit: 'g',
    pivotBase: 100,
    ingredients: [
      { label: 'Eau', base: 5000, unit: 'mL' },
    ],
    notes: 'Eau frémissante (pas pleine ébullition), couper la cuisson avant de mettre le thé, 4 tours sens horaire + 1 contraire, infuser 10 min. DLC 2 jours.',
  },
  tapioca: {
    nom: 'Tapioca',
    icon: Wheat,
    color: 'text-orange-600',
    bgColor: 'bg-orange-50/50 border-orange-200',
    pivotLabel: 'Tapioca',
    pivotUnit: 'kg',
    pivotBase: 1,
    ingredients: [
      { label: 'Eau', base: 5000, unit: 'mL' },
      { label: 'Sucre', base: 500, unit: 'g' },
    ],
    notes: 'Respecter le ratio : pour 1kg de tapioca, 5L d\'eau et 500g de sucre.',
  },
};

function fmt(n) {
  if (!isFinite(n)) return '—';
  if (n >= 1000) return Number(n.toFixed(0)).toLocaleString('fr-FR');
  if (n >= 100) return Number(n.toFixed(0)).toLocaleString('fr-FR');
  if (n >= 10) return Number(n.toFixed(1)).toLocaleString('fr-FR');
  return Number(n.toFixed(2)).toLocaleString('fr-FR');
}

function CalculatorCard({ recipe, recipeKey }) {
  const [pivot, setPivot] = useState(recipe.pivotBase);
  const [unit, setUnit] = useState(recipe.pivotUnit); // unite affichee (g ou kg)

  // pivot exprime en gramme pour le calcul
  const pivotInGrams = unit === 'kg' ? pivot * 1000 : pivot;
  const baseInGrams = recipe.pivotUnit === 'kg' ? recipe.pivotBase * 1000 : recipe.pivotBase;
  const ratio = baseInGrams > 0 ? pivotInGrams / baseInGrams : 0;
  const Icon = recipe.icon;

  const reset = () => { setPivot(recipe.pivotBase); setUnit(recipe.pivotUnit); };

  // Toggle unite : convertit la valeur affichee
  const switchUnit = (newUnit) => {
    if (newUnit === unit) return;
    if (newUnit === 'kg' && unit === 'g') setPivot(parseFloat((pivot / 1000).toFixed(3)));
    if (newUnit === 'g' && unit === 'kg') setPivot(parseFloat((pivot * 1000).toFixed(2)));
    setUnit(newUnit);
  };

  // Presets en grammes (la valeur est ensuite convertie pour affichage selon unit)
  const presetsG = recipeKey === 'tapioca'
    ? [500, 1000, 1500, 2000, 3000]
    : [12.5, 25, 50, 100, 150, 250];

  return (
    <Card className={`${recipe.bgColor}`} data-testid={`calc-${recipeKey}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Icon className={`w-5 h-5 ${recipe.color}`} /> {recipe.nom}
        </CardTitle>
        <CardDescription className="text-xs">
          Base : <b>{recipe.pivotBase} {recipe.pivotUnit}</b> de {recipe.pivotLabel.toLowerCase()} •
          {' '}
          {recipe.ingredients.map(i => `${i.base} ${i.unit} ${i.label.toLowerCase()}`).join(' • ')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Pivot input + unit toggle */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <Label className="text-sm font-semibold">Quantité de {recipe.pivotLabel}</Label>
            <div className="inline-flex items-center rounded-md border bg-background p-0.5">
              <button
                type="button"
                onClick={() => switchUnit('g')}
                className={`px-2.5 py-1 text-xs font-semibold rounded transition ${unit === 'g' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}
                data-testid={`unit-g-${recipeKey}`}
              >g</button>
              <button
                type="button"
                onClick={() => switchUnit('kg')}
                className={`px-2.5 py-1 text-xs font-semibold rounded transition ${unit === 'kg' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}
                data-testid={`unit-kg-${recipeKey}`}
              >kg</button>
            </div>
          </div>
          <div className="flex gap-2 items-center">
            <Input
              type="number"
              step={unit === 'kg' ? '0.01' : '0.1'}
              value={pivot}
              onChange={e => setPivot(parseFloat(e.target.value) || 0)}
              className="text-2xl h-14 font-bold tabular-nums max-w-[180px]"
              data-testid={`pivot-${recipeKey}`}
            />
            <span className="text-2xl font-semibold text-muted-foreground">{unit}</span>
            <Button variant="ghost" size="sm" onClick={reset} title="Réinitialiser">
              <RotateCcw className="w-4 h-4" />
            </Button>
          </div>
          {/* Quick presets — affiches dans l'unite courante */}
          <div className="flex flex-wrap gap-1 pt-1">
            {presetsG.map(pg => {
              const display = unit === 'kg' ? pg / 1000 : pg;
              const active = Math.abs(pivotInGrams - pg) < 0.001;
              return (
                <Button
                  key={pg}
                  size="sm"
                  variant={active ? 'default' : 'outline'}
                  className="h-7 px-2 text-xs"
                  onClick={() => setPivot(display)}
                  data-testid={`preset-${recipeKey}-${pg}`}
                >
                  {display} {unit}
                </Button>
              );
            })}
          </div>
        </div>

        {/* Computed ingredients */}
        <div className="space-y-2 pt-3 border-t">
          <Label className="text-xs uppercase text-muted-foreground tracking-wide">Ingrédients à préparer</Label>
          {recipe.ingredients.map(ing => {
            const computed = ing.base * ratio;
            // Affichage intelligent : conversion mL→L et g→kg si grand
            let displayValue = computed;
            let displayUnit = ing.unit;
            if (ing.unit === 'mL' && computed >= 1000) {
              displayValue = computed / 1000;
              displayUnit = 'L';
            } else if (ing.unit === 'g' && computed >= 1000) {
              displayValue = computed / 1000;
              displayUnit = 'kg';
            }
            return (
              <div key={ing.label} className="flex items-center justify-between p-2.5 rounded-md bg-background border">
                <span className="font-medium">{ing.label}</span>
                <div className="text-right">
                  <span className="text-2xl font-bold tabular-nums" data-testid={`result-${recipeKey}-${ing.label}`}>
                    {fmt(displayValue)}
                  </span>
                  <span className="text-sm text-muted-foreground ml-1">{displayUnit}</span>
                  {displayUnit !== ing.unit && (
                    <p className="text-[10px] text-muted-foreground">({fmt(computed)} {ing.unit})</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Ratio badge */}
        <div className="flex items-center gap-2 pt-2 border-t">
          <Badge variant="outline" className="font-mono">×{ratio.toFixed(2)}</Badge>
          <span className="text-xs text-muted-foreground">par rapport à la base</span>
        </div>

        {/* Notes */}
        {recipe.notes && (
          <p className="text-xs text-muted-foreground italic border-l-2 border-muted pl-3">
            💡 {recipe.notes}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export function CalculateurPrepPage() {
  return (
    <div className="space-y-4 pb-8" data-testid="calculateur-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Calculator className="w-6 h-6 text-purple-600" />
          Calculateur de préparation
        </h1>
        <p className="text-muted-foreground text-sm">
          Change la quantité du produit principal — les autres ingrédients se recalculent automatiquement (règle de trois)
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        <CalculatorCard recipeKey="jasmin" recipe={RECIPES.jasmin} />
        <CalculatorCard recipeKey="oolong" recipe={RECIPES.oolong} />
        <CalculatorCard recipeKey="tapioca" recipe={RECIPES.tapioca} />
      </div>

      <Card className="border-amber-200 bg-amber-50/30">
        <CardContent className="pt-4">
          <p className="text-sm">
            <b>📐 Comment ça marche :</b> entre la quantité de thé ou tapioca souhaitée, l'app applique
            une règle de trois sur tous les ingrédients. Exemple : 75 g de Jasmin → eau = 5000 × (75/50) = <b>7500 mL (7,5 L)</b>.
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            Les recettes complètes sont disponibles dans <b>Recettes Secrètes</b> → catégorie <b>The</b>.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
